import {
    Box,
    Breadcrumbs,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ORTHOIMAGE_RGB_TGDT_UUID,
    PHOTOGRAMMETRY_JOB_NAME,
    PIPELINE_INSTANCE_STATUS,
} from "../../constants";

import { BACKEND_ROUTES } from "../../backendRoutes";
import { BreadcrumbChip } from "../../components";
import DownloadIcon from "@mui/icons-material/Download";
import { DownloadProjectButton } from "./DownloadProjectButton";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { OrthoimageMap } from "./OrthoimageMap";
import PropTypes from "prop-types";
import { camelCaseToTitleCase } from "../../utils";
import { formatDateTime } from "../../utils/formatTime";
import { getPipelineStatus } from "../utils";
import { useFetch } from "../../hooks";
import useSWR from "swr";

const ORTHOIMAGE_RGB_NAME_PREFIX = "orthomosaicRgb";
const REPORT_RGB_NAME_PREFIX = "reportRgb";
const LOG_NAME_PREFIX = "log";

// For certain photogrammetry outputs, we display a download button for them to be downloadable
// with a simple click. This list contains the name prefixes of these outputs.
const DIRECTLY_DOWNLOADABLE_OUTPUT_PREFIXES = [
    ORTHOIMAGE_RGB_NAME_PREFIX,
    REPORT_RGB_NAME_PREFIX,
    LOG_NAME_PREFIX,
];

const DOWNLOAD_COMMAND_BUTTON_VISIBLE_STATUSES = [
    PIPELINE_INSTANCE_STATUS.FINISHED,
    PIPELINE_INSTANCE_STATUS.ERROR,
    PIPELINE_INSTANCE_STATUS.ABORTED,
];

RgbAlignmentReport.propTypes = {
    missionUuid: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

export function RgbAlignmentReport({ missionUuid, onClose }) {
    const { get } = useFetch();

    const {
        data: mission,
        error: missionFetchError,
        isLoading: missionIsLoading,
    } = useSWR(
        `${BACKEND_ROUTES.MISSION}/${missionUuid}?parentInfo=true&acquisitionVector=true&pipelineInstancesTGDTUuid=${ORTHOIMAGE_RGB_TGDT_UUID}`
    );

    // Get the latest pipeline instance of the mission for rgb orthoimage processing
    const pipelineInstance =
        mission?.AssociatedTraitGroups[0]?.PipelineInstances[0];

    // Get the process uuid of the pipeline instance's photogrammetry step
    const photogrammetryJob =
        pipelineInstance?.translatedPipeline?.spec.jobs.find(
            (job) => job.name === PHOTOGRAMMETRY_JOB_NAME
        );
    const processUuid = photogrammetryJob?.processUuid;

    // Get the output binary data of the photogrammetry process
    const {
        data: outputBinaryData,
        error: outputBinaryDataFetchError,
        isLoading: outputBinaryDataIsLoading,
    } = useSWR(
        processUuid
            ? `${BACKEND_ROUTES.PROCESS}/${processUuid}${BACKEND_ROUTES.OUTPUT_BINARY_DATA_LIST}`
            : null
    );

    // Get the output binary data representing the RGB orthoimage
    const rgbOrthoOutputBinaryData = outputBinaryData?.rows.find((obd) =>
        obd.name.startsWith(ORTHOIMAGE_RGB_NAME_PREFIX)
    );

    // Get the array of directly downloadable photogrammetry output binary data
    const directlyDownloadableOutputBinaryData = outputBinaryData?.rows
        .filter((obd) =>
            DIRECTLY_DOWNLOADABLE_OUTPUT_PREFIXES.some((prefix) =>
                obd.name.startsWith(prefix)
            )
        )
        .toSorted((a, b) => a.name.localeCompare(b.name));

    const onClickDownload = async (s3Location) => {
        const presignedUrl = await get(
            `${BACKEND_ROUTES.PUBLIC_SIGNED_URL}?key=${s3Location}`
        );
        if (presignedUrl) window.open(presignedUrl, "_blank");
    };

    const mergedFetchError = missionFetchError ?? outputBinaryDataFetchError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    const isLoading = missionIsLoading || outputBinaryDataIsLoading;
    if (isLoading)
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="15em"
            />
        );

    const { Icon, title, color } = getPipelineStatus(pipelineInstance?.status);

    return (
        <>
            <DialogTitle>
                <Stack direction="row" spacing={2}>
                    <Box>Alignment Report</Box>
                    <Breadcrumbs separator="/">
                        <BreadcrumbChip
                            label={mission.Site.Contract.Company.name}
                        />
                        <BreadcrumbChip label={mission.Site.Contract.name} />
                        <BreadcrumbChip label={mission.Site.name} />
                        <BreadcrumbChip label={formatDateTime(mission.date)} />
                    </Breadcrumbs>
                </Stack>
            </DialogTitle>
            <Divider variant="middle" />
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        {pipelineInstance ? (
                            <Typography
                                sx={{
                                    display: "inline-flex",
                                    columnGap: 1,
                                }}
                            >
                                Orthoimage processing status:
                                <Tooltip title={title} placement="right">
                                    {Icon ? (
                                        <Icon color={color} />
                                    ) : (
                                        <>&#x2300;</>
                                    )}
                                </Tooltip>
                            </Typography>
                        ) : (
                            <Typography>
                                No orthoimage processing found
                            </Typography>
                        )}
                    </Grid>

                    <Grid size="grow">
                        {(!pipelineInstance ||
                            DOWNLOAD_COMMAND_BUTTON_VISIBLE_STATUSES.includes(
                                pipelineInstance?.status
                            )) && (
                            <DownloadProjectButton
                                pipelineInstance={pipelineInstance}
                                mission={mission}
                            />
                        )}
                    </Grid>

                    <Grid size="auto">
                        <Stack spacing={1} direction="row">
                            {directlyDownloadableOutputBinaryData?.map(
                                (obd) => (
                                    <Button
                                        key={obd.uuid}
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={() =>
                                            onClickDownload(obd.s3Location)
                                        }
                                    >
                                        {camelCaseToTitleCase(obd.name)}
                                    </Button>
                                )
                            )}
                        </Stack>
                    </Grid>

                    {rgbOrthoOutputBinaryData && (
                        <Grid size={12}>
                            <Box sx={{ height: "calc(100vh - 350px)" }}>
                                <OrthoimageMap
                                    rgbOrthoOutputBinaryData={
                                        rgbOrthoOutputBinaryData
                                    }
                                />
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <Divider variant="middle" />
            <DialogActions>
                <Button type="button" onClick={() => onClose()}>
                    Close
                </Button>
            </DialogActions>
        </>
    );
}
